<script>
    import { createEventDispatcher, onMount } from 'svelte';
    import DraggableResizablePanel from '../shared/DraggableResizablePanel.svelte';
    import { uploadChemistryTable, formatRGroupData, copyToClipboard } from '../../services/chemistryService';
    import { chemistryDataStore } from '../../stores/chemistryDataStore';

    export let show = false;

    const dispatch = createEventDispatcher();

    let selectedFile = null;
    let genericMolecule = '';
    let isUploading = false;
    let uploadError = '';
    let uploadSuccess = '';
    let fileInput;
    let results = null;
    let copySuccess = '';
    let processingProgress = 0;
    let processingStage = '';
    let estimatedMolecules = 0;

    // Load chemistry data from store on mount (persists during session, clears on page refresh)
    onMount(() => {
        const storedResults = $chemistryDataStore;
        if (storedResults) {
            results = storedResults;
            // Show message about loaded session with filename
            uploadSuccess = `Previously uploaded: ${storedResults.filename || 'chemistry table'} (${storedResults.num_processed} molecules processed)`;
        }
    });

    function close() {
        dispatch('close');
    }

    function handleFileSelect(event) {
        const file = event.target.files[0];
        if (file) {
            const validExtensions = ['.csv', '.xlsx'];
            const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

            if (!validExtensions.includes(fileExtension)) {
                uploadError = 'Please select a .csv or .xlsx file';
                selectedFile = null;
                if (fileInput) fileInput.value = '';
                return;
            }

            selectedFile = file;
            uploadError = '';
            uploadSuccess = '';
        }
    }

    async function handleUpload() {
        if (!selectedFile) {
            uploadError = 'Please select a file';
            return;
        }

        isUploading = true;
        uploadError = '';
        uploadSuccess = '';
        results = null;
        processingProgress = 0;
        processingStage = '';

        // Estimate molecules from file size (rough estimate: ~100 bytes per molecule)
        estimatedMolecules = Math.max(10, Math.floor(selectedFile.size / 100));

        // Determine if we have a scaffold
        const hasScaffold = genericMolecule && genericMolecule.trim();

        // Start fake progress bar
        startProgressSimulation(hasScaffold, estimatedMolecules);

        try {
            const response = await uploadChemistryTable(selectedFile, genericMolecule);

            if (response.success) {
                // Complete the progress
                processingProgress = 100;
                processingStage = 'Complete';

                results = response;
                uploadSuccess = `Successfully processed ${response.num_processed} molecules with R-group decomposition`;

                // Save to store (persists during session, clears on page refresh)
                chemistryDataStore.set(results);

                // Don't reset form - allow user to modify and re-process
            } else {
                uploadError = 'R-group decomposition failed';
            }
        } catch (error) {
            uploadError = error.message || 'Failed to upload chemistry data';
            console.error('Chemistry upload error:', error);
        } finally {
            isUploading = false;
            // Clear progress after a delay
            setTimeout(() => {
                processingProgress = 0;
                processingStage = '';
            }, 1000);
        }
    }

    function startProgressSimulation(hasScaffold, moleculeCount) {
        // Initialize progress immediately
        processingProgress = 1;

        if (hasScaffold) {
            // With scaffold: faster and more predictable
            // Estimated time: ~10ms per molecule (based on real data: 1200 molecules in 10 seconds)
            const estimatedTime = Math.max(3000, moleculeCount * 10); // At least 3 seconds
            const updateInterval = 100; // Update every 100ms for smooth animation
            const totalSteps = estimatedTime / updateInterval;

            processingStage = `Processing ${moleculeCount} molecules...`;

            let currentStep = 0;
            const progressInterval = setInterval(() => {
                currentStep++;
                processingProgress = Math.min(95, (currentStep / totalSteps) * 100);

                if (currentStep >= totalSteps || !isUploading) {
                    clearInterval(progressInterval);
                }
            }, updateInterval);
        } else {
            // Without scaffold: slower due to MCS discovery
            // Phase 1: MCS discovery (10-30 seconds depending on molecule count)
            // Phase 2: R-group decomposition (~10ms per molecule)

            const mcsTime = Math.min(30000, 10000 + moleculeCount * 50); // 10-30 seconds
            const decompositionTime = moleculeCount * 10;
            const totalTime = mcsTime + decompositionTime;

            let currentTime = 0;
            const interval = 100; // Update every 100ms

            const progressInterval = setInterval(() => {
                currentTime += interval;

                if (currentTime < mcsTime) {
                    // MCS discovery phase (takes up 60-80% of progress)
                    processingStage = 'Discovering common scaffold...';
                    processingProgress = Math.min(80, (currentTime / mcsTime) * 80);
                } else {
                    // R-group decomposition phase (20-40% of progress)
                    processingStage = `Decomposing ${moleculeCount} molecules...`;
                    const decompositionProgress = ((currentTime - mcsTime) / decompositionTime) * 20;
                    processingProgress = Math.min(95, 80 + decompositionProgress);
                }

                if (currentTime >= totalTime || !isUploading) {
                    clearInterval(progressInterval);
                }
            }, interval);
        }
    }

    function handleClearFile() {
        selectedFile = null;
        if (fileInput) fileInput.value = '';
        uploadError = '';
        uploadSuccess = '';
    }

    function clearResults() {
        // Just close the results panel without clearing stored data
        results = null;
        uploadSuccess = '';
        copySuccess = '';
    }

    function clearData() {
        // Clear everything - results, form inputs, and store
        results = null;
        uploadSuccess = '';
        copySuccess = '';
        uploadError = '';
        selectedFile = null;
        genericMolecule = '';
        if (fileInput) fileInput.value = '';

        // Clear the store (removes data from session)
        chemistryDataStore.set(null);
    }

    function getDisplayColumns(columns) {
        if (!columns) return [];
        // Filter out Core column from display
        return columns.filter(col => col !== 'Core');
    }

    function formatForClaim1() {
        if (!results || !results.rgroup_data || !results.columns) {
            return '';
        }

        // Get R-group columns (exclude SMILES, Matched, and Core)
        const rgroupColumns = results.columns.filter(col =>
            col !== 'SMILES' && col !== 'Matched' && col !== 'Core'
        );

        // Build formatted string
        const lines = [];
        for (const rgroupLabel of rgroupColumns) {
            // Collect all unique non-null values for this R-group
            const values = new Set();
            for (const row of results.rgroup_data) {
                let value = row[rgroupLabel];
                if (value && value !== '-' && value !== null && value !== undefined) {
                    // Remove attachment point notation like [*:1], [*:2], etc.
                    value = value.replace(/\[\*:\d+\]/g, '');
                    // Remove all square brackets (e.g., [H] becomes H)
                    value = value.replace(/[\[\]]/g, '');
                    values.add(value);
                }
            }

            // Format as: {R-group} is {value1}, {value2}, ...;
            if (values.size > 0) {
                const valuesList = Array.from(values).join(', ');
                lines.push(`${rgroupLabel} is ${valuesList};`);
            }
        }

        return lines.join('\n');
    }

    async function handleCopyForClaim1() {
        const formatted = formatForClaim1();
        if (!formatted) {
            copySuccess = 'No R-group data to copy';
            return;
        }

        const success = await copyToClipboard(formatted);
        if (success) {
            copySuccess = 'Copied to clipboard!';
            setTimeout(() => {
                copySuccess = '';
            }, 3000);
        } else {
            copySuccess = 'Failed to copy';
        }
    }
</script>

<DraggableResizablePanel
    {show}
    title="Chemistry Tables"
    initialPosition={{ x: 200, y: 150 }}
    initialWidth={600}
    initialHeight={500}
    minWidth={500}
    minHeight={400}
    maxWidth={800}
    maxHeight={700}
    storageKey="chemistry-tables"
    theme="navigation"
    on:close={close}
>
    <div class="chemistry-modal">
        <div class="upload-form">
            <h3>Import Chemistry Data</h3>
            <p class="section-description">
                Upload a table of small molecules with their properties.
                Optionally provide a generic molecule structure.
            </p>
            <div class="form-group">
                <label for="file-input">
                    Molecule Table (.csv or .xlsx) <span class="required">*</span>
                </label>
                <input
                    id="file-input"
                    type="file"
                    accept=".csv,.xlsx"
                    bind:this={fileInput}
                    on:change={handleFileSelect}
                    disabled={isUploading}
                />
                {#if selectedFile}
                    <div class="selected-file">
                        Selected: {selectedFile.name}
                    </div>
                {/if}
            </div>

            <div class="form-group">
                <label for="generic-molecule">
                    Generic Molecule (SMILES String)
                </label>
                <input
                    id="generic-molecule"
                    type="text"
                    bind:value={genericMolecule}
                    placeholder="e.g., CCO or CC(=O)O"
                    disabled={isUploading}
                />
                <p class="input-hint">
                    Enter a SMILES string representing the generic molecular structure
                </p>
            </div>

            {#if uploadError}
                <div class="error-message">
                    {uploadError}
                </div>
            {/if}

            {#if uploadSuccess}
                <div class="success-message">
                    {uploadSuccess}
                </div>
            {/if}

            {#if isUploading}
                <div class="progress-container">
                    <div class="progress-header">
                        <span class="progress-stage">{processingStage || 'Initializing...'}</span>
                        <span class="progress-percentage">{Math.round(processingProgress)}%</span>
                    </div>
                    <div class="progress-bar">
                        <div class="progress-fill" style="width: {processingProgress}%"></div>
                    </div>
                </div>
            {/if}

            <button
                class="modal-button modal-button-accent"
                on:click={handleUpload}
                disabled={isUploading || !selectedFile}
            >
                {isUploading ? 'Processing...' : 'Process'}
            </button>
        </div>

        {#if results}
            <div class="results-section">
                <div class="results-header">
                    <h3>R-Group Decomposition Results</h3>
                    <div class="results-header-actions">
                        <button class="clear-data-button" on:click={clearData} title="Clear all data and start fresh">
                            Clear Data
                        </button>
                        <button class="close-results" on:click={clearResults} title="Close results panel">×</button>
                    </div>
                </div>

                <div class="results-summary">
                    <div class="summary-item">
                        <span class="label">Scaffold SMILES:</span>
                        <code class="value">{results.scaffold_smiles || 'Auto-detected'}</code>
                    </div>
                    <div class="summary-item">
                        <span class="label">Molecules Processed:</span>
                        <span class="value">{results.num_processed} / {results.num_molecules}</span>
                    </div>
                    {#if results.num_failed > 0}
                        <div class="summary-item warning">
                            <span class="label">Failed:</span>
                            <span class="value">{results.num_failed}</span>
                        </div>
                    {/if}
                </div>

                {#if copySuccess}
                    <div class="copy-success-message">
                        {copySuccess}
                    </div>
                {/if}

                <button
                    class="modal-button modal-button-secondary copy-button"
                    on:click={handleCopyForClaim1}
                >
                    Copy for Claim 1
                </button>

                {#if results.rgroup_data && results.rgroup_data.length > 0}
                    <div class="results-table-wrapper">
                        <table class="results-table">
                            <thead>
                                <tr>
                                    {#each getDisplayColumns(results.columns) as col}
                                        <th>{col}</th>
                                    {/each}
                                </tr>
                            </thead>
                            <tbody>
                                {#each results.rgroup_data as row}
                                    <tr>
                                        {#each getDisplayColumns(results.columns) as col}
                                            <td>{row[col] !== undefined && row[col] !== null ? row[col] : '-'}</td>
                                        {/each}
                                    </tr>
                                {/each}
                            </tbody>
                        </table>
                    </div>
                {/if}
            </div>
        {/if}
    </div>
</DraggableResizablePanel>

<style>
.chemistry-modal {
    /* Remove height: 100% to allow overflow scrolling in parent .draggable-panel-content */
    padding: var(--spacing-4);
}

.upload-form {
    background: var(--color-surface-content-spec);
    padding: var(--spacing-4);
    border-radius: var(--border-radius-lg);
    margin-bottom: var(--spacing-5);
    border: 1px solid var(--color-bg-panel-30);
}

.upload-form h3 {
    margin: 0 0 var(--spacing-2) 0;
    font-size: var(--font-size-lg);
    color: var(--color-text-primary);
}

.section-description {
    margin: 0 0 var(--spacing-4) 0;
    font-size: var(--font-size-sm);
    color: var(--color-text-secondary);
    line-height: 1.5;
}

.form-group {
    margin-bottom: var(--spacing-4);
}

.form-group label {
    display: block;
    margin-bottom: var(--spacing-2);
    font-weight: var(--font-weight-medium);
    color: var(--color-text-primary);
}

.form-group input[type="text"],
.form-group input[type="file"] {
    width: 100%;
    padding: var(--spacing-2);
    border: 1px solid var(--color-bg-panel-30);
    border-radius: var(--border-radius-md);
    background: var(--color-surface-content-spec);
    color: var(--color-text-primary);
    font-size: var(--font-size-sm);
}

.required {
    color: var(--color-error);
}

.selected-file {
    padding: 8px;
    margin: 10px 0;
    background: var(--color-bg-main);
    border: 1px solid var(--color-term-border);
    border-radius: 4px;
    font-size: 0.9rem;
    color: var(--color-text-primary);
}

.input-hint {
    margin: 4px 0 0 0;
    font-size: var(--font-size-xs);
    color: var(--color-text-tertiary);
    font-style: italic;
}

.error-message {
    color: var(--color-error);
    padding: 10px;
    margin: 10px 0;
    background: var(--color-ambiguous-term-error-bg);
    border-radius: 4px;
}

.success-message {
    color: var(--color-text-primary);
    padding: 10px;
    margin: 10px 0;
    background: var(--color-bg-main);
    border: 1px solid var(--color-term-border);
    border-radius: 4px;
}

.upload-form .modal-button {
    padding: var(--spacing-2) var(--spacing-4);
    font-size: var(--font-size-sm);
}

.progress-container {
    margin: var(--spacing-4) 0;
    padding: var(--spacing-3);
    background: var(--color-bg-main);
    border: 1px solid var(--color-term-border);
    border-radius: var(--border-radius-md);
}

.progress-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--spacing-2);
}

.progress-stage {
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    color: var(--color-text-primary);
}

.progress-percentage {
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-secondary);
}

.progress-bar {
    width: 100%;
    height: 8px;
    background: var(--color-bg-secondary);
    border-radius: 4px;
    overflow: hidden;
}

.progress-fill {
    height: 100%;
    background: linear-gradient(90deg, var(--color-terms-600), var(--color-terms-500));
    border-radius: 4px;
    transition: width 0.3s ease;
}

.copy-button {
    width: 100%;
    margin-top: var(--spacing-3);
    padding: var(--spacing-2) var(--spacing-4);
    font-size: var(--font-size-sm);
}

.copy-success-message {
    color: var(--color-text-primary);
    padding: 10px;
    margin: 10px 0;
    background: var(--color-bg-main);
    border: 1px solid var(--color-term-border);
    border-radius: 4px;
    text-align: center;
}

.results-section {
    margin-top: var(--spacing-5);
    padding-top: var(--spacing-5);
    border-top: 1px solid var(--color-bg-panel-30);
}

.results-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--spacing-4);
}

.results-header h3 {
    margin: 0;
    font-size: var(--font-size-lg);
    color: var(--color-text-primary);
}

.results-header-actions {
    display: flex;
    align-items: center;
    gap: var(--spacing-2);
}

.clear-data-button {
    padding: var(--spacing-1) var(--spacing-3);
    font-size: var(--font-size-sm);
    background: var(--color-error);
    color: white;
    border: none;
    border-radius: var(--border-radius-md);
    cursor: pointer;
    transition: opacity 0.2s ease;
}

.clear-data-button:hover {
    opacity: 0.8;
}

.close-results {
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--color-text-secondary);
}

.close-results:hover {
    color: var(--color-text-primary);
}

.results-summary {
    background: var(--color-surface-content-spec);
    border: 1px solid var(--color-bg-panel-30);
    border-radius: var(--border-radius-lg);
    padding: var(--spacing-3);
    margin-bottom: var(--spacing-4);
}

.summary-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: var(--spacing-1) 0;
    font-size: var(--font-size-sm);
}

.summary-item .label {
    color: var(--color-text-secondary);
}

.summary-item .value {
    color: var(--color-text-primary);
}

.summary-item code.value {
    font-family: var(--font-family-mono);
    font-size: var(--font-size-xs);
    background: var(--color-bg-main);
    padding: 2px 6px;
    border-radius: 3px;
    word-break: break-all;
    white-space: normal;
}

.summary-item.warning .label,
.summary-item.warning .value {
    color: var(--color-error);
}

.results-table-wrapper {
    overflow-x: auto;
    overflow-y: auto;
    max-height: 300px; /* Reduced to show ~10 rows */
    border: 1px solid var(--color-term-border);
    border-radius: 4px;
    background: var(--color-bg-terms-toolbar);
}

/* Darker scrollbar styling for table */
.results-table-wrapper::-webkit-scrollbar {
    width: 8px;
    height: 8px;
}

.results-table-wrapper::-webkit-scrollbar-track {
    background: var(--color-scrollbar-track);
}

.results-table-wrapper::-webkit-scrollbar-thumb {
    background: var(--color-text-secondary);
    border-radius: 3px;
    opacity: 1;
}

.results-table-wrapper::-webkit-scrollbar-thumb:hover {
    background: var(--color-text-primary);
}

.results-table-wrapper::-webkit-scrollbar-corner {
    background: var(--color-scrollbar-track);
}

/* Firefox scrollbar styling */
.results-table-wrapper {
    scrollbar-width: auto;
    scrollbar-color: var(--color-text-secondary) var(--color-scrollbar-track);
}

.results-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.75rem;
}

.results-table th,
.results-table td {
    padding: 6px 8px;
    border: 1px solid var(--color-term-border);
    text-align: left;
    white-space: nowrap;
}

.results-table th {
    position: sticky;
    top: 0;
    background: var(--color-btn-default);
    font-weight: 600;
    font-size: 0.7rem;
    color: var(--color-text-primary);
    z-index: 10;
}

.results-table td {
    background: var(--color-bg-main);
    color: var(--color-text-primary);
}

/* Scrollbar styling */
.chemistry-modal::-webkit-scrollbar {
    width: 8px;
}

.chemistry-modal::-webkit-scrollbar-track {
    background: transparent;
}

.chemistry-modal::-webkit-scrollbar-thumb {
    background: var(--color-border-medium);
    border-radius: 4px;
}

.chemistry-modal::-webkit-scrollbar-thumb:hover {
    background: var(--color-border-dark);
}
</style>
